<template>
  <div>
    <form-wrapper v-if="passportType" :key="stepFormData.passport_type || 'no-type'">
      <form-group>
        <form-group-title>
          {{ $t('application-form.step-2.id-document-subtitle') }}
        </form-group-title>
        <validation-provider rules="required">
          <icu-select
            :options="passportChoices"
            :value="stepFormData.passport_type"
            :get-option-value="opt => opt.codename"
            :label="$t('application-form.step-2.document-type')"
            required
            :placeholder="$t('application-form.step-2.document-type-hint')"
            @input="emitInput('passport_type', $event)"
          />
        </validation-provider>
        <checkbox-input
          v-if="isPassportInternational"
          :value="stepFormData.is_biometric"
          @input="emitInput('is_biometric', $event)"
        >
          {{ $t('application-form.step-2.my-passport-is-biometric') }}
        </checkbox-input>
        <validation-provider
          v-if="showCountry"
          v-slot="{errors}"
          rules="required"
        >
          <icu-select
            :errors="[...errors, ...getFieldErrors('country')]"
            :value="stepFormData.country"
            :label="$t('application-form.step-2.country')"
            :options="getCountries"
            :placeholder="isResident? 'Україна': 'Ukraine'"
            :searchable="true"
            required
            @input="emitInput('country', $event)"
          />
        </validation-provider>
        <validation-provider
          v-if="showEddr"
          v-slot="{errors}"
          slim
          :rules="{eddr_number: true}"
        >
          <text-input
            mask="00000000{-}00000"
            :errors="[...errors, ...getFieldErrors('reestr_number')]"
            :value="stepFormData.reestr_number"
            :label="$t(reestrNumberFieldUIData.label)"
            :required="isEddrRequired"
            :hint="$t(reestrNumberHint)"
            @input="emitReestrNumber"
          />
        </validation-provider>
        <validation-provider
          v-slot="{errors}"
          slim
          :rules="seriesFieldUIData.rules"
        >
          <text-input
            v-if="!isPassportID"
            :errors="[...errors, ...getFieldErrors('seria')]"
            :value="stepFormData.seria"
            :label="$t('application-form.step-2.series')"
            placeholder="AC"
            :mask-options="seriesFieldUIData.maskOptions"
            :required="seriesFieldUIData.rules.required"
            :hint="$t(seriesFieldUIData.hint)"
            @input="emitInput('seria', $event)"
          />
        </validation-provider>
        <validation-provider
          v-slot="{errors}"
          :rules="numberFieldUIData.rules"
        >
          <text-input
            :errors="[...errors, ...getFieldErrors('number')]"
            :value="stepFormData.number"
            :label="$t(numberFieldUIData.label)"
            :mask-options="numberFieldUIData.maskOptions"
            :placeholder="'610491000'.slice(0, numberFieldUIData.rules.length)"
            required
            :hint="$t('application-form.step-2.passport-id-number-hint')"
            @input="emitInput('number', $event)"
          />
        </validation-provider>
        <validation-provider v-slot="{errors}" rules="required">
          <date-input
            required
            placeholder="14.06.2000"
            :v-calendar-options="{maxDate: $options.todayDate}"
            :errors="[...errors, ...getFieldErrors('date_issued')]"
            :value="stepFormData.date_issued"
            :label="$t('application-form.step-2.date-of-issue')"
            :hint="$t('application-form.step-2.date-of-issue-hint')"
            @input="emitInput('date_issued', $event)"
          />
        </validation-provider>
        <validation-provider
          v-if="showValidUntil"
          v-slot="{errors}"
          slim
          :rules="{
            required: validUntilFieldUIData.required,
            ua_valid_until: stepFormData.date_issued,
            valid_until_more_then_10: {isPassportID: isPassportID, issuedDate: stepFormData.date_issued}
          }"
        >
          <date-input
            :errors="[...errors, ...getFieldErrors('valid_until')]"
            :v-calendar-options="{minDate: minValidUntilDate}"
            :value="stepFormData.valid_until"
            :label="$t('application-form.step-2.date-of-expiry')"
            placeholder="14.06.2000"
            :required="validUntilFieldUIData.required"
            :hint="$t('application-form.step-2.date-of-expiry-hint')"
            @input="emitInput('valid_until', $event)"
          />
        </validation-provider>
        <validation-provider
          v-slot="{errors}"
          slim
          :rules="issuedFieldUIData.rules"
        >
          <text-input
            :errors="[...errors, ...getFieldErrors('issued')]"
            :value="stepFormData.issued"
            :label="$t(issuedFieldUIData.label)"
            :placeholder="issuedFieldUIData.placeholder"
            :required="issuedFieldUIData.rules.required"
            :mask="issuedFieldUIData.mask"
            :hint="$t(issuedFieldUIData.hint)"
            @input="emitInput('issued', $event)"
          />
        </validation-provider>

        <validation-provider v-slot="{errors, validate}" rules="required_files|files_has_no_errors">
          <icu-dropzone
            :errors="[...getFieldErrors('photo'), ...errors]"
            :value="stepFormData.photo"
            :label="$t(photoFieldUIData.label)"
            :maximum-files="photoFieldUIData.maxCount"
            :is-resident="isResident"
            required
            :hint="$t(photoFieldUIData.hint)"
            @input="emitFile('photo', $event)"
            @delete-file="deleteFile('photo', $event)"
            @files-uploaded="validate"
          />
        </validation-provider>
        <validation-provider v-if="!isResident" v-slot="{errors, validate}" rules="required_files|files_has_no_errors">
          <icu-dropzone
            :errors="[ ...getFieldErrors('photo_selfie'), ...errors]"
            :value="stepFormData.photo_selfie"
            :label="$t(`application-form.step-2.photo-selfie-label${!isResident ?'-nonresident': '' }`)"
            :maximum-files="1"
            :is-resident="isResident"
            required
            :hint="$t('application-form.step-2.photo-selfie-hint')"
            @input="emitFile('photo_selfie', $event)"
            @delete-file="deleteFile('photo_selfie', $event)"
            @files-uploaded="validate"
          />
        </validation-provider>


        <validation-provider v-slot="{errors, validate}" slim rules="required_files|files_has_no_errors">
          <icu-dropzone
            v-if="isPassportInternational || isPassportID"
            :errors="[...getFieldErrors('photo_register'), ...errors]"
            :value="stepFormData.photo_register"
            :is-resident="isResident"
            :label="$t('application-form.step-2.certificate-of-residence')"
            :hint="$t('application-form.step-2.certificate-of-residence-hint')"
            required
            @input="emitFile('photo_register', $event)"
            @delete-file="deleteFile('photo_register', $event)"
            @files-uploaded="validate"
          />
        </validation-provider>


        <!-- TEMPORARILY HIDDEN -->
        <!--        <validation-provider v-slot="{errors, validate}" slim rules="required_files|files_has_no_errors">-->
        <!--          <icu-dropzone-->
        <!--            :errors="[...errors, ...getFieldErrors('photo_45')]"-->
        <!--            :value="stepFormData.photo_45"-->
        <!--            :label="$t('application-form.step-2.second-photo')"-->
        <!--            required-->
        <!--            :hint="$t('application-form.step-2.second-photo-hint')"-->
        <!--            @input="emitFile('photo_45', $event)"-->
        <!--            @delete-file="deleteFile('photo_45', $event)"-->
        <!--            @files-uploaded="validate"-->
        <!--          />-->
        <!--        </validation-provider>-->
        <!-- /TEMPORARILY HIDDEN -->

        <!--        <validation-provider v-slot="{errors, validate}" slim rules="required_files|files_has_no_errors">
          <icu-dropzone
            v-if="isPassportOld && isOlderThan45"
            :errors="[...getFieldErrors('photo_45', ...errors)]"
            :value="stepFormData.photo_45"
            :label="$t('application-form.step-2.second-photo')"
            required
            :hint="$t('application-form.step-2.second-photo-hint')"
            @input="emitFile('photo_45', $event)"
            @delete-file="deleteFile('photo_45', $event)"
            @files-uploaded="validate"
          />
        </validation-provider>-->
      </form-group>
    </form-wrapper>
  </div>

</template>

<script>
import FormWrapper from '@/components/views/RegisterPage/FormWrapper.vue'
import FormGroup from '@/components/views/RegisterPage/FormGroup.vue'
import IcuSelect from '@/components/inputs/IcuSelect.vue'
import TextInput from '@/components/inputs/TextInput.vue'
import DateInput from '@/components/inputs/DateInput.vue'
import IcuDropzone from '@/components/inputs/IcuDropzone.vue'
import FormGroupTitle from '@/components/views/RegisterPage/FormGroupTitle'
import {ValidationProvider} from 'vee-validate'
import {constants} from '@/constants.js'
import CheckboxInput from '@/components/inputs/CheckboxInput.vue'

export default {
  name: 'SecondStep',
  components: { FormGroupTitle, CheckboxInput, IcuDropzone, ValidationProvider, DateInput, TextInput, IcuSelect, FormGroup, FormWrapper },
  props: {
    stepFormData: {
      type: Object,
      required: true
    },
    isOlderThan45: {
      type: Boolean,
      default: false
    },
    backendErrors: {
      type: Object,
      default: () => {}
    },
    passportChoices: {
      type: Array,
      default: () => []
    },
    countries:{
      type: Array,
      default:()=>[]
    },
    citizenship:{
      type: String,
      required: true
    },
    isResident:{
      type: Boolean,
      required: true
    }
  },
  todayDate: new Date(),
  maximumPhotosByPassport: {
    [constants.PASSPORT_TYPES.OLD_PASSPORT]: 6,
    [constants.PASSPORT_TYPES.CARD_ID]: 2,
    [constants.PASSPORT_TYPES.INTERNATIONAL_PASSPORT]: 1,
  },
  computed: {
    getCountries() {
      const countries = [...this.countries]
      const indexUA = countries.findIndex(el => el.id === 'UA')
      if (indexUA > -1) {
        countries.splice(indexUA, 1)
      }
      return countries
    },
    reestrNumberHint(){
      if(this.isPassportInternational) {
        return 'application-form.step-2.reestr-number-foreign-hint'
      }
      if(this.isPassportID) {
        return 'application-form.step-2.reestr-number-hint'
      }
      return false
    },
    showValidUntil() {
      return  !this.isPassportOld
    },
    showCountry() {
      return this.isForeignPermanentResidence || this.isForeignTemporaryResidence
    },
    showEddr() {
      return this.isPassportID || this.isPassportInternationalBiometric
    },
    isEddrRequired() {
      return this.isPassportID || this.isPassportInternationalBiometric
    },
    minValidUntilDate() {
      if (!this.stepFormData.date_issued) {
        return null
      }

      const date = new Date(this.stepFormData.date_issued)

      date.setDate(date.getDate() + 1)

      return date
    },
    numberFieldUIData() {
      const data = {
        rules: {required: true},
        label: 'application-form.step-2.passport-id-number',
        maskOptions: {mask:/^[A-Za-z0-9 \-.]+$/}
      }

      if (this.isPassportID) {
        data.label = 'application-form.step-2.id-card-number'
        data.maskOptions.mask = /^[0-9\s]{0,9}$/
        data.rules.length = 9
      } else if (this.isPassportOld) {
        data.maskOptions.mask = /^[0-9\s]{0,6}$/
        data.rules.length = 6
      } else if (this.isPassportInternational) {
        data.maskOptions.mask = /^[0-9\s]{0,6}$/
        data.rules.length = 6
      } else if( this.isForeignTemporaryResidence || this.isForeignPermanentResidence) {
        data.label = 'application-form.step-2.id-number'
        data.rules = {required: true,}
      } else if(this.isForeignPassportId){
        data.label = 'application-form.step-2.id-card-number'
      } else if( this.isTemporaryResidence || this.isPermanentResidence) {
        data.maskOptions.mask = /^[0-9\s]+$/
        data.label='application-form.step-2.id-number'
        data.rules = {required: true}
      }

      return data
    },
    seriesFieldUIData() {

      const data = {
        rules: {
          required: false
        },
        maskOptions: {
          mask: /^[A-Z0-9\s]{0,256}$/,
          prepare: str => str.toUpperCase(),
          lazy: true
        },
        hint: ''
      }

      if (this.isPassportOld) {
        data.hint = 'application-form.step-2.series-hint'
        data.maskOptions.mask = /^[A-ZА-ЩЬЮЯҐЄІЇ0-9\s]{0,2}$/
        data.required = data.rules.required =  true
      } else if (this.isPassportInternational) {
        data.hint = 'application-form.step-2.series-international-hint'

        data.maskOptions.mask = /^[A-Z0-9\s]{0,2}$/
        data.rules.required = true
      } else if (this.isPassportID) {
        data.hint = 'application-form.step-2.series-international-hint'
        data.maskOptions.mask = /^[A-Z0-9\s]{0,2}$/
      }  else {
        data.hint = 'application-form.step-2.series-international-number-hint'
        data.rules.ua_passport_series_latin_number = true
      }

      return data
    },
    reestrNumberFieldUIData() {
      const data = {
        label: '',
      }

      if (this.isPassportID) {
        data.label = 'application-form.step-2.reestr-number-id-card'
      } else if (this.isPassportOld) {
        data.label = 'application-form.step-2.reestr-number'
      } else if (this.isPassportInternational) {
        data.label = 'application-form.step-2.reestr-number'
      }

      return data
    },
    photoFieldUIData() {
      const fieldData = {
        hint: 'application-form.step-2.photo-doc-hint',
        label: 'application-form.step-2.photo-doc-label',
        maxCount: 6
      }

      if( !this.isResident){
        fieldData.label = 'application-form.step-2.photo-doc-nonresident'
        return fieldData
      }

      if( this.isPassportOld) {
        fieldData.label = 'application-form.step-2.photo-passport-label'
        fieldData.hint = 'application-form.step-2.photo-passport-hint'
      }else if(this.isForeignPassport) {
        if( this.isResident) {
          fieldData.label = 'application-form.step-2.photo-passport-label'
        } else {
          fieldData.label = 'application-form.step-2.photo-passport-label-non-citizen'
        }
        fieldData.hint = 'application-form.step-2.photo-foreign-passport-hint'
      } else if( this.isPassportID) {
        fieldData.label = 'application-form.step-2.photo-id-label'
        fieldData.hint = 'application-form.step-2.photo-id-hint'
        fieldData.maxCount = 2
      } else if (this.isForeignPassportId) {
        fieldData.label = 'application-form.step-2.photo-id-label'
        fieldData.hint = 'application-form.step-2.photo-foreign-id-hint'
        fieldData.maxCount = 2
      } else if(this.isPassportInternational || this.isForeignInternationalPassport){
        fieldData.label = 'application-form.step-2.photo-int-passport-label'
        fieldData.hint = 'application-form.step-2.photo-int-passport-hint'
        fieldData.maxCount = 1
      }

      return fieldData
    },
    validUntilFieldUIData(){
      const fieldData = {
        required:false
      }
      if(
          this.isPassportID
          || this.isPassportInternational
          || this.isPermanentResidence
          || this.isTemporaryResidence
          || this.isForeignPassportId
          || this.isTemporaryPermit
          || this.isTemporaryCertificate
          || this.isAdditionalProtectionCard
      ){
        fieldData.required = true
      }
      return fieldData
    },
    issuedFieldUIData() {
      const fieldData = {
        hint: 'application-form.step-2.issuing-authority-text-hint',
        label: 'application-form.step-2.issuing-authority',
        placeholder: '',
        mask: undefined,
        rules: {
          required: false,
          max: 256,
          min: 0,
          ua_letters_space_numeric: this.isResident,
          neres_letters_space_numeric_dash: !this.isResident
        },
      }

      if (this.isPassportOld) {
        fieldData.placeholder = 'Шацьким РВ УМВС України у Волинській обл'
        fieldData.rules.required = true
        fieldData.min = 2
        fieldData.rules.ua_letters_space_numeric = false
        fieldData.rules.ua_letters_space_numeric_dash = true
      }
      if (this.isPassportID || this.isPassportInternational) {
        fieldData.placeholder = '1234'
        fieldData.rules.required = true
        fieldData.mask = '0000'
      }
      if(this.isPassportInternational
        || this.isTemporaryPermit
        || this.isTemporaryCertificate
        || this.isAdditionalProtectionCard ){
        fieldData.hint = 'application-form.step-2.issuing-authority-code-hint'
        fieldData.label = 'application-form.step-2.issuing-authority-code'
      }

      if (this.isTemporaryResidence || this.isPermanentResidence) {
        fieldData.rules ={
          length:4,
          numeric: true
        }
        fieldData.rules.required = true
      }
      if(this.isTemporaryPermit || this.isTemporaryCertificate || this.isAdditionalProtectionCard) {
        fieldData.rules.required = true
      }

      return fieldData
    },
    isPassportOld() {
      return this.passportType === constants.PASSPORT_TYPES.OLD_PASSPORT
    },
    isPassportID() {
      return this.passportType === constants.PASSPORT_TYPES.CARD_ID
    },
    isPassportInternational() {
      return this.passportType === constants.PASSPORT_TYPES.INTERNATIONAL_PASSPORT
    },
    isPassportInternationalBiometric() {
      return this.isPassportInternational && this.stepFormData.is_biometric
    },
    isTemporaryResidence() {
      return this.passportType === constants.PASSPORT_TYPES.TEMPORARY_RESIDENCE
    },
    isPermanentResidence() {
      return this.passportType === constants.PASSPORT_TYPES.PERMANENT_RESIDENCE
    },
    isTemporaryPermit() {
      return this.passportType === constants.PASSPORT_TYPES.TEMPORARY_PERMIT
    },
    isTemporaryCertificate() {
      return this.passportType === constants.PASSPORT_TYPES.TEMPORARY_CERTIFICATE
    },
    isAdditionalProtectionCard() {
      return this.passportType === constants.PASSPORT_TYPES.ADDITIONAL_PROTECTION_CARD
    },

    isForeignTemporaryResidence() {
      return this.passportType === constants.PASSPORT_TYPES.FOREIGN_TEMPORARY_RESIDENCE
    },
    isForeignPermanentResidence() {
      return this.passportType === constants.PASSPORT_TYPES.FOREIGN_PERMANENT_RESIDENCE
    },
    isForeignPassportId() {
      return this.passportType === constants.PASSPORT_TYPES.FOREIGN_CARD_ID
    },
    isForeignInternationalPassport() {
      return this.passportType === constants.PASSPORT_TYPES.FOREIGN_INTERNATIONAL_PASSPORT
    },
    isForeignPassport() {
      return this.passportType === constants.PASSPORT_TYPES.FOREIGN_PASSPORT
    },
    passportType() {
      return this.stepFormData?.passport_type
    }
  },
  watch:{
    citizenship(){
      this.setCountry()
    }
  },
  mounted() {
    this.setCountry()
  },
  methods: {
    setCountry(){
      const country = this.stepFormData.country || this.citizenship
      this.emitInput('country', country)
    },
    emitReestrNumber(value) {
      if (value === '-') {
        value = ''
      }
      this.emitInput('reestr_number', value)
    },
    emitInput(formKey, value) {
      this.$emit('update-field', {formKey, value})
    },
    emitFile(formKey, file) {
      this.$emit('add-file', {formKey, file})
    },
    deleteFile(formKey, file) {
      this.$emit('delete-file', {formKey, file})
    },
    getFieldErrors(fieldName) {
      return this.backendErrors[fieldName] ?? []
    }
  }
}
</script>
